import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useAuth } from '@workos-inc/authkit-react'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { onboardingSearchSchema } from '@/lib/onboarding-search'
import { OnboardingWizard } from '@/components/onboarding'
import { useOnboardingStatus } from '@/api'

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
  validateSearch: onboardingSearchSchema,
})

export function OnboardingPage() {
  const navigate = useNavigate()
  const searchParams = useSearch({ from: '/onboarding' })
  const { user, isLoading: authLoading, signIn } = useAuth()
  
  // CRITICAL: Save promo code to sessionStorage IMMEDIATELY on render
  // This must happen synchronously BEFORE signIn() redirects to WorkOS
  // We do this outside useEffect so it runs before any auth redirect
  if (searchParams.code) {
    sessionStorage.setItem('onboarding_promo_code', searchParams.code)
  }
  
  // Redirect to sign in if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      signIn()
    }
  }, [authLoading, user, signIn])
  
  // Fetch onboarding status - always get fresh data on this page
  const { data: status, isLoading: statusLoading, error } = useOnboardingStatus({
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: 'always',
  })
  
  // If user doesn't need onboarding, redirect to dashboard
  useEffect(() => {
    if (status?.has_inbox && !searchParams.manual) {
      navigate({ to: '/setup' })
    }
  }, [status, navigate, searchParams.manual])
  
  // Show loading while auth or status is loading
  if (authLoading || !user || statusLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }
  
  // Show error if status fetch failed
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-destructive">Failed to load onboarding status</p>
          <button 
            onClick={() => window.location.reload()}
            className="text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }
  
  // Render wizard with initial status
  if (status) {
    return <OnboardingWizard initialStatus={status} />
  }
  
  return null
}
